"use client";
import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { Book, ChevronDown, House, LibraryBig } from "lucide-react";
import { ID, US } from "country-flag-icons/react/3x2";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCookies } from "next-client-cookies";
import { toast } from "sonner";
import OrganizationSwitcher from "./OrganizationSwitcher";
import ProfileDropdown from "./ProfileDropdown";


const languageOptions = [
  { code: "id", label: "Bahasa Indonesia", Flag: ID },
  { code: "en", label: "English", Flag: US },
];

const DEFAULT_LOCALE = "en";

const Navbar = () => {
  const t = useTranslations("navbar");
  const links = [
    { href: "/", icon: House, label: t("dashboard") },
    { href: "/ebook", icon: Book, label: t("keloola_book") },
    { href: "/bookshelf", icon: LibraryBig, label: t("bookshelf") },
  ];
  
  const pathname = usePathname();
  const router = useRouter();
  const cookies = useCookies();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isHamburgerMenuOpen, setIsHamburgerMenuOpen] = useState(false);
  const currentLocale = cookies.get("locale") || DEFAULT_LOCALE;

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (!target.classList.contains(".dropdown-container")) {
      setIsDropdownOpen(false);
    }
  };

  const toggleHamburgerMenu = () => {
    setIsHamburgerMenuOpen((prev) => !prev);
  };

  const handleLanguageChange = (langCode: string) => {
    cookies.set("locale", langCode);
    toast.success(t("language_change_success"));
    router.refresh();
  };

  useEffect(() => {
    if (isDropdownOpen) {
      window.addEventListener("click", handleOutsideClick);
    } else {
      window.removeEventListener("click", handleOutsideClick);
    }

    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, [isDropdownOpen]);

  return (
    <>
      <nav
        className={`font-comfortaa flex items-center bg-[#CD2834] px-4 md:px-8 py-4 text-white z-50 relative`}
      >
        {/* Navbar content */}
        <div className="flex min-w-0 flex-auto items-center gap-2">
          <Image
            src="/assets/icons/logo.svg"
            alt="Keloola Book"
            width={32}
            height={32}
          />
          <h1 className="text-nowrap text-xl md:text-2xl">Keloola Book</h1>
          <OrganizationSwitcher />
        </div>

        {/* Hamburger Icon */}
        <button
          id="hamburger-btn"
          className="block md:hidden text-white focus:outline-none cursor-pointer"
          onClick={toggleHamburgerMenu}
        >
          <div className="flex flex-col gap-1">
            <span
              className={`block w-6 h-0.5 bg-white transition-all ${
                isHamburgerMenuOpen ? "rotate-45 translate-y-1.5" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-white ${
                isHamburgerMenuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-white transition-all ${
                isHamburgerMenuOpen ? "-rotate-45 -translate-y-1.5" : ""
              }`}
            />
          </div>
        </button>

        {/* Navigation Menu */}
        <div className="flex justify-between flex-none md:flex-auto">
          <ul
            className={`menu ${
              isHamburgerMenuOpen ? "block" : "hidden"
            } md:flex flex-col md:flex-row gap-4 font-poppins font-normal absolute md:static top-16 left-0 w-full md:w-auto bg-white text-black px-8 py-4 md:py-0 z-40 shadow-lg md:bg-transparent md:shadow-none md:text-white`}
          >
            <li className="flex flex-col pb-4 md:flex-row gap-2 justify-start md:hidden cursor-pointer">
              <ProfileDropdown mobile />
            </li>
            <div className="flex flex-col md:flex-row gap-0 md:gap-4">
              {links.map((link) => {
                const Icon = link.icon;
                return (
                  <li
                    key={link.href}
                    className={`flex items-center cursor-pointer my-2 md:my-0`}
                    onClick={toggleHamburgerMenu}
                  >
                    <Link
                      href={link.href}
                      className={`relative flex gap-2 items-center text-black md:text-white pb-2 ${
                        (
                          link.href === "/"
                            ? pathname === "/"
                            : pathname.startsWith(link.href)
                        )
                          ? "after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-0.5 after:bg-white"
                          : ""
                      }`}
                    >
                      <Icon size={24} />
                      <span>{link.label}</span>
                    </Link>
                  </li>
                );
              })}
            </div>
          </ul>

          {/* md+ devices flag and profile*/}
          <div className="relative hidden md:flex items-center gap-8">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 focus:outline-none">
                {currentLocale === "id" ? (
                  <ID className="w-6 h-4" />
                ) : (
                  <US className="w-6 h-4" />
                )}{" "}
                <ChevronDown className="w-5 h-5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={`font-poppins font-normal w-50 bg-white rounded-md shadow-lg text-black`}
                align="center"
              >
                {languageOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.code}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                    onClick={() => handleLanguageChange(option.code)}
                  >
                    <option.Flag className="w-5 h-3.5" />
                    <span>{option.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <ProfileDropdown />
          </div>
        </div>
      </nav>
      <div
        className={`navbar-overlay ${
          isHamburgerMenuOpen ? "fixed" : "hidden"
        } top-0 left-0 w-full h-full bg-black bg-opacity-50 z-30 md:hidden`}
      ></div>
    </>
  );
};

export default Navbar;
